// Export the API base URL so it can be used by other modules
export const API_BASE_URL = 'https://api.priorixa.in/finance';

// Token storage utilities
const getStoredToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch {
    return true;
  }
};

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Get token and check if it's expired
    const token = getStoredToken();
    if (token && isTokenExpired(token)) {
      localStorage.removeItem('auth_token');
      throw new Error('Token expired');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    // Add authorization header if token exists
    if (token && !isTokenExpired(token)) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      headers,
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Category API methods
  async getCategories(parentId?: number): Promise<any[]> {
    const params = parentId !== undefined ? `?parent_id=${parentId}` : '';
    return this.request(`/categories/${params}`);
  }

  async getCategoriesTree(): Promise<any[]> {
    return this.request('/categories/tree');
  }

  async createCategory(data: any): Promise<any> {
    return this.request('/categories/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(categoryId: number, data: any): Promise<any> {
    return this.request(`/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(categoryId: number): Promise<void> {
    return this.request(`/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // Expense API methods
  async getExpenses(filters?: any): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    return this.request(`/expenses/?${queryString}`);
  }

  async createExpense(data: any): Promise<any> {
    return this.request('/expenses/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExpense(expenseId: number, data: any): Promise<any> {
    return this.request(`/expenses/${expenseId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteExpense(expenseId: number): Promise<void> {
    return this.request(`/expenses/${expenseId}`, {
      method: 'DELETE',
    });
  }

  async getExpenseSummary(filters?: any): Promise<any> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    return this.request(`/expenses/summary/total?${queryString}`);
  }

  async bulkUploadExpenses(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/expenses/bulk-upload`;

    // Get token and check if it's expired
    const token = getStoredToken();
    if (token && isTokenExpired(token)) {
      localStorage.removeItem('auth_token');
      throw new Error('Token expired');
    }

    const headers: Record<string, string> = {};

    // Add authorization header if token exists
    if (token && !isTokenExpired(token)) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers,
      // Don't set Content-Type header - let browser set it with boundary for FormData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Analytics API methods
  async getSpendingTrends(filters?: any): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    return this.request(`/expenses/analytics/trends?${queryString}`);
  }

  async getSpendingByCategory(filters?: any): Promise<any> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    return this.request(`/expenses/analytics/by-category?${queryString}`);
  }

  async getTopCategories(filters?: any): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    return this.request(`/expenses/analytics/top-categories?${queryString}`);
  }

  // Investment API methods - Asset Types
  async getAssetTypes(): Promise<any[]> {
    return this.request('/asset-types/');
  }

  async getAssetType(assetTypeId: number): Promise<any> {
    return this.request(`/asset-types/${assetTypeId}`);
  }

  // Investment API methods - Assets
  async getAssets(): Promise<any[]> {
    return this.request('/assets/');
  }

  async getAsset(assetId: number): Promise<any> {
    return this.request(`/assets/${assetId}`);
  }

  async createAsset(data: any): Promise<any> {
    return this.request('/assets/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAsset(assetId: number, data: any): Promise<any> {
    return this.request(`/assets/${assetId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAsset(assetId: number): Promise<void> {
    return this.request(`/assets/${assetId}`, {
      method: 'DELETE',
    });
  }

  async refreshAssetPrice(assetId: number): Promise<any> {
    return this.request(`/assets/${assetId}/refresh-price`, {
      method: 'POST',
    });
  }

  // Investment API methods - Transactions
  async getTransactions(assetId?: number): Promise<any[]> {
    const params = assetId !== undefined ? `?asset_id=${assetId}` : '';
    return this.request(`/transactions/${params}`);
  }

  async getTransaction(transactionId: number): Promise<any> {
    return this.request(`/transactions/${transactionId}`);
  }

  async createTransaction(data: any): Promise<any> {
    return this.request('/transactions/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTransaction(transactionId: number, data: any): Promise<any> {
    return this.request(`/transactions/${transactionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTransaction(transactionId: number): Promise<void> {
    return this.request(`/transactions/${transactionId}`, {
      method: 'DELETE',
    });
  }

  async getAssetHoldings(assetId: number): Promise<any> {
    return this.request(`/transactions/asset/${assetId}/holdings`);
  }

  // Investment API methods - Portfolio
  async getPortfolioSummary(): Promise<any> {
    return this.request('/portfolio/summary');
  }

  async getPortfolioAllocation(): Promise<any[]> {
    return this.request('/portfolio/allocation');
  }

  async getPortfolioHoldings(): Promise<any[]> {
    return this.request('/portfolio/holdings');
  }

  async getPortfolioPerformance(): Promise<any> {
    return this.request('/portfolio/performance');
  }
}

export const apiClient = new ApiClient();